import { useCallback, useEffect, useRef, useState } from 'react'

import { Typography } from '@twistopayments/design-system/components/atoms/typography'

import type { SectionsQuotesCarouselComponent } from '@twisto/api-cms'

import { Hidden } from 'homepage/components/atoms/hidden'

import { Quote } from './quote'
import * as styles from './quotes-carousel.styles'

const SLIDE_INTERVAL = 3000

export const QuotesCarousel = ({
  title,
  subtitle,
  quotes,
}: SectionsQuotesCarouselComponent) => {
  const carouselRef = useRef<HTMLDivElement>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const scrollToIndex = useCallback((index: number) => {
    const carousel = carouselRef.current

    if (!carousel) {
      return
    }

    const quoteElements = carousel.children
    const target = quoteElements[index] as HTMLElement | undefined

    if (target) {
      carousel.scrollTo({
        left: target.offsetLeft,
        behavior: 'smooth',
      })
    }
  }, [])

  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) =>
        quotes && quotes.length > 0 ? (prev + 1) % quotes.length : 0
      )
    }, SLIDE_INTERVAL)
  }, [quotes])

  const resetAutoSlide = useCallback(() => {
    startAutoSlide()
  }, [startAutoSlide])

  useEffect(() => {
    startAutoSlide()

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [startAutoSlide])

  useEffect(() => {
    const carousel = carouselRef.current

    if (!carousel || !quotes || quotes.length === 0) {
      return
    }

    let timeoutId: NodeJS.Timeout

    const handleScroll = () => {
      clearTimeout(timeoutId)

      timeoutId = setTimeout(() => {
        const quoteElements = Array.from(carousel.children) as HTMLElement[]
        const scrollLeft = carousel.scrollLeft

        let newIndex = 0
        let minDistance = Infinity

        quoteElements.forEach((el, index) => {
          const distance = Math.abs(el.offsetLeft - scrollLeft)

          if (distance < minDistance) {
            minDistance = distance
            newIndex = index
          }
        })

        setActiveIndex(newIndex)
        resetAutoSlide()
      }, 100)
    }

    carousel.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      carousel.removeEventListener('scroll', handleScroll)
      clearTimeout(timeoutId)
    }
  }, [quotes, resetAutoSlide])

  useEffect(() => {
    if (!carouselRef.current || !quotes || quotes.length === 0) {
      return
    }

    scrollToIndex(activeIndex)
  }, [activeIndex, quotes, scrollToIndex])

  const handleNext = () => {
    const nextIndex = quotes ? (activeIndex + 1) % quotes.length : 0

    setActiveIndex(nextIndex)
    resetAutoSlide()
  }

  const handlePrev = () => {
    const prevIndex = quotes
      ? (activeIndex - 1 + quotes.length) % quotes.length
      : 0

    setActiveIndex(prevIndex)
    resetAutoSlide()
  }

  return (
    <section css={styles.section}>
      <div css={styles.container}>
        <Hidden breakpoint="smMdDown">
          <button
            css={[styles.carouselControl, styles.prevButton]}
            onClick={handlePrev}
          >
            {'\u276E'}
          </button>
        </Hidden>
        <div css={styles.header}>
          <p css={styles.title}>{title}</p>
          <Typography css={styles.subtitle} fontWeight="heavy" variant="h2">
            {subtitle}
          </Typography>
        </div>
        <div css={styles.quotes}>
          <div ref={carouselRef} css={styles.carousel}>
            {quotes?.map((quote) => <Quote key={quote.id} {...quote} />)}
          </div>
        </div>
        <Hidden breakpoint="smMdDown">
          <button
            css={[styles.carouselControl, styles.nextButton]}
            onClick={handleNext}
          >
            {'\u276F'}
          </button>
        </Hidden>
      </div>
      <Hidden breakpoint="smMdUp">
        <ol css={styles.controls}>
          {quotes?.map(({ id }, index) => (
            <li key={id}>
              <button
                css={[
                  styles.indicator,
                  index === activeIndex && styles.activeIndicator,
                ]}
                onClick={() => {
                  setActiveIndex(index)
                  resetAutoSlide()
                }}
              />
            </li>
          ))}
        </ol>
      </Hidden>
    </section>
  )
}
